'use client';

import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import './Calender.scss';
import { fetchGetLawConsultList } from '@module/apis/client-side/law';
import SettingConfig from '@module/config/setting';
import { cn } from '@module/lib/utils';

interface Event {
  start: string;
  end: string;
  name: string;
}

interface EventList {
  [date: string]: Event[];
}

const formatTime = (dateTime: string): string => {
  return dayjs(dateTime).format('HH:mm');
};

export default function Calender() {
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEvents, setSelectedEvents] = useState<Event[]>([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [schedule, setSchedule] = useState<EventList>({});

  const generateCalendarDates = () => {
    const firstDay = currentDate.startOf('month');
    const startDay = firstDay.day() === 0 ? firstDay.subtract(6, 'day') : firstDay.subtract(firstDay.day(), 'day');

    const days = [];
    for (let i = 0; i < 6; i++) {
      // 6 weeks
      const week = [];
      for (let j = 0; j < 7; j++) {
        // 7 days
        const day = startDay.add(i * 7 + j, 'day');
        week.push(day);
      }
      days.push(week);

      if (i > 3 && week[6].month() !== currentDate.month()) {
        break;
      }
    }
    return days;
  };

  useEffect(() => {
    const fetchSchedule = async () => {
      try {
        const res = await fetchGetLawConsultList({ date: currentDate.format('YYYY-MM') });
        setSchedule(res.list);
      } catch (_err) {
        //
      }
    };
    fetchSchedule();
  }, [currentDate]);

  const prevMonth = () => setCurrentDate(currentDate.subtract(1, 'month'));
  const nextMonth = () => setCurrentDate(currentDate.add(1, 'month'));

  const showEvents = (date: string) => {
    if (schedule?.[date]) {
      setSelectedEvents(schedule[date]);
      setSelectedDate(date);
      setIsModalOpen(true);
    }
  };

  const hasEvents = (date: string) => {
    return schedule?.[date] && schedule[date].length > 0;
  };

  const today = dayjs().format('YYYY-MM-DD');

  const calendarRows = generateCalendarDates();

  return (
    <div className='calender-container' id='reservation'>
      <div className='content'>
        <div className='calender-top'>
          <h4>캘린더</h4>
          <div className='row'>
            <div className='calender-nav prev-nav'>
              <div className='prev' onClick={prevMonth}>
                <img
                  src={`${SettingConfig.S3_BUCKET_URL}/img/law/calendar_left.png`}
                  alt='이전 달'
                  width={36}
                  height={36}
                />
              </div>
            </div>
            <div className='title-day'>{currentDate.format('YYYY년 MM월')}</div>
            <div className='calender-nav next-nav'>
              <div className='next' onClick={nextMonth}>
                <img
                  src={`${SettingConfig.S3_BUCKET_URL}/img/law/calendar_right.png`}
                  alt='다음 달'
                  width={36}
                  height={36}
                />
              </div>
            </div>
          </div>
        </div>

        <div className='calender-body'>
          <ul className='head'>
            <li>SUN</li>
            <li>MON</li>
            <li>TUE</li>
            <li>WED</li>
            <li>THU</li>
            <li>FRI</li>
            <li>SAT</li>
          </ul>

          <div className='body-box'>
            {calendarRows.map((week, i) => {
              // if all of the days in the week are not the current month, hide the week
              if (week.every((day) => day.month() !== currentDate.month())) {
                return null;
              }

              return (
                <ul key={i}>
                  {week.map((day, j) => {
                    const dateString = day.format('YYYY-MM-DD');
                    const isToday = dateString === today;
                    const isCurrentMonth = day.month() === currentDate.month();

                    const isPrevMonthOfChosenMonth = day.isBefore(currentDate.startOf('month'), 'day');
                    const isNextMonth = day.isAfter(currentDate.endOf('month'), 'day');
                    if (isNextMonth) return null;
                    return (
                      <li
                        key={j}
                        className={cn({
                          '!bg-white': isPrevMonthOfChosenMonth,
                          today: isToday,
                          active: isCurrentMonth,
                          '!bg-[#f9f9f9] !outline-none': isNextMonth,
                        })}
                      >
                        {isCurrentMonth && (
                          <>
                            <div className='date'>{day.format('D')}</div>
                            {hasEvents(dateString) && (
                              <div className='event' onClick={() => showEvents(dateString)}>
                                <div className='event-desc'>예약일정</div>
                              </div>
                            )}
                          </>
                        )}
                      </li>
                    );
                  })}
                </ul>
              );
            })}
          </div>
        </div>
      </div>

      {isModalOpen && (
        <div className='calendar-modal' onClick={() => setIsModalOpen(false)}>
          <div className='modal-content' onClick={(e) => e.stopPropagation()}>
            <div className='modal-header'>예약 일정 - {dayjs(selectedDate).format('YYYY년 MM월 DD일')}</div>
            <div className='close-button' onClick={() => setIsModalOpen(false)}>
              X
            </div>

            {selectedEvents.map((event, index) => (
              <div key={index} className='event-item'>
                <span className='event-time'>
                  {formatTime(event.start)} ~ {formatTime(event.end)}
                </span>
                <span className='event-desc'>{event.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
